<template>
  <el-dialog
    v-model="dialogVisible"
    title="修改密码"
    :width="mustUpdatePassword ? '800px' : '510px'"
    class="password-dialog"
    :class="{ 'is-first-login': mustUpdatePassword }"
    align-center
    :close-on-click-modal="false"
    :close-on-press-escape="!mustUpdatePassword"
    :show-close="!mustUpdatePassword"
  >
    <div
      class="password-dialog__tip"
      :class="{ 'is-first-login': mustUpdatePassword }"
    >
      <el-icon><InfoFilled /></el-icon>
      <div v-if="mustUpdatePassword" class="password-dialog__tip-content">
        <p>尊敬的客户，您好！</p>
        <p>感谢您使用瑞秋国际物流大客户平台。首次登录前，请阅读以下说明：</p>
        <p>
          <strong>管理员账号：</strong
          >您的管理员账号由系统根据瑞秋客户管理代码自动生成，账号格式为「客户代码-admin」；
        </p>
        <p>
          <strong>初始密码：</strong
          >初始密码为系统随机生成的八位数字，由瑞秋公司对应业务员向您发放；
        </p>
        <p>
          <strong>首次登录激活：</strong
          >管理员首次登录需先修改初始密码，完成激活后方可正常使用；
        </p>
        <p>
          <strong>子账号管理：</strong
          >子账号由管理员在平台内自行创建，平台后台将对子账号的创建及使用进行统一监管。
        </p>
      </div>
      <span v-else>为保障账号安全，修改成功后请使用新密码重新登录。</span>
    </div>

    <el-form
      ref="passwordFormRef"
      class="password-form"
      :model="form"
      :rules="rules"
      label-position="top"
      @submit.prevent
    >
      <el-form-item label="原密码" prop="oldPassword">
        <el-input
          v-model.trim="form.oldPassword"
          type="password"
          show-password
          autocomplete="current-password"
          placeholder="请输入原密码"
        />
      </el-form-item>

      <el-form-item label="新密码" prop="newPassword">
        <el-input
          v-model.trim="form.newPassword"
          type="password"
          show-password
          autocomplete="new-password"
          placeholder="请输入不少于 6 位的字母数字组合"
        />
      </el-form-item>

      <el-form-item label="确认新密码" prop="confirmPassword">
        <el-input
          v-model.trim="form.confirmPassword"
          type="password"
          show-password
          autocomplete="new-password"
          placeholder="请再次输入新密码"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="password-dialog__footer">
        <el-button v-if="!mustUpdatePassword" @click="dialogVisible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          :disabled="!canSubmit"
          :loading="submitting"
          @click="triggerSubmit"
        >
          确认修改
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:visible"]);
const { proxy } = getCurrentInstance();
const { $api } = proxy;
const store = useStore();
const router = useRouter();
const passwordFormRef = ref(null);
const submitting = ref(false);

const form = reactive({
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
});

const currentUserName = computed(() => store.getters.userInfo?.userName || "");
const mustUpdatePassword = computed(
  () => String(store.getters.userInfo?.newUserFlag) === "1"
);
const dialogVisible = computed({
  get: () => mustUpdatePassword.value || props.visible,
  set: (value) => {
    if (!mustUpdatePassword.value) {
      emit("update:visible", value);
    }
  },
});
const canSubmit = computed(
  () =>
    Boolean(
      form.oldPassword.trim() &&
        form.newPassword.trim() &&
        form.confirmPassword.trim()
    ) && !submitting.value
);

const validateNewPassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error("请输入新密码"));
  } else if (value.length < 6) {
    callback(new Error("密码长度不能少于 6 位"));
  } else if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) {
    callback(new Error("密码必须包含字母和数字"));
  } else if (value === form.oldPassword) {
    callback(new Error("新密码不能与原密码相同"));
  } else {
    callback();
  }
};

const validateConfirmPassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error("请再次输入新密码"));
  } else if (value !== form.newPassword) {
    callback(new Error("两次输入密码不一致"));
  } else {
    callback();
  }
};

const rules = {
  oldPassword: [{ required: true, message: "请输入原密码", trigger: "blur" }],
  newPassword: [
    { required: true, message: "请输入新密码", trigger: "blur" },
    { validator: validateNewPassword, trigger: "blur" },
  ],
  confirmPassword: [
    { required: true, message: "请再次输入新密码", trigger: "blur" },
    { validator: validateConfirmPassword, trigger: "blur" },
  ],
};

watch(dialogVisible, (value) => {
  if (value) return;
  passwordFormRef.value?.resetFields?.();
  Object.assign(form, {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
});

const triggerSubmit = async () => {
  if (!canSubmit.value) return;
  const valid = await passwordFormRef.value?.validate().catch(() => false);
  if (valid) {
    await submit();
  }
};

const submit = async () => {
  if (submitting.value) return;

  submitting.value = true;
  try {
    await $api.user.firstLoginUpdate({
      oldPassword: form.oldPassword,
      newPassword: form.newPassword,
    });
    await store.dispatch("LogInit");
    await router.replace("/login");
    ElMessage.success("密码修改成功，请使用新密码重新登录");
  } finally {
    submitting.value = false;
  }
};
</script>

<style lang="scss">
.password-dialog {
  --password-brand-color: #f5822a;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 510px;
  height: 550px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  min-width: 0;
  padding: 0 !important;
  overflow: hidden;
  color: var(--app-text-color);
  background: var(--app-surface-color);
  border-radius: 8px;

  &.is-first-login {
    width: 800px;
    height: 650px;
  }

  .el-dialog__header {
    box-sizing: border-box;
    display: flex;
    flex: 0 0 66px;
    align-items: center;
    height: 66px !important;
    min-height: 66px !important;
    padding: 0 !important;
    margin: 0;
    border-bottom: 1px solid var(--app-border-color) !important;
  }

  .el-dialog__title {
    padding-left: 20px !important;
    color: var(--app-text-color);
    font-size: 16px;
    font-weight: 600;
    line-height: 66px;
  }

  .el-dialog__headerbtn {
    width: 54px;
    height: 66px;
    color: var(--app-text-secondary-color);
  }

  .el-dialog__close {
    font-size: 16px;
  }

  .el-dialog__body {
    box-sizing: border-box;
    flex: 1 1 auto;
    min-height: 0;
    padding: 15px 20px !important;
    overflow-x: hidden;
    overflow-y: auto;
    color: var(--app-text-color);
    background: var(--app-surface-color);
  }

  .el-dialog__footer {
    box-sizing: border-box;
    display: flex;
    flex: 0 0 80px;
    align-items: center;
    width: 100%;
    height: 80px !important;
    padding: 0 20px !important;
    background: var(--app-surface-color);
    border-top: 0;
    box-shadow: none;
  }
}

.password-dialog__tip {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 9px 13px;
  color: #2f80ed;
  background: color-mix(in srgb, #2f80ed 10%, var(--app-surface-color));
  border: 1px solid color-mix(in srgb, #2f80ed 18%, var(--app-border-color));

  .el-icon {
    flex: 0 0 auto;
    margin-right: 10px;
    font-size: 20px;
  }

  span {
    font-size: var(--app-font-size);
    line-height: 20px;
  }

  &.is-first-login {
    align-items: flex-start;

    .el-icon {
      margin-top: 1px;
    }
  }
}

.password-dialog__tip-content {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--app-font-size);
  line-height: 20px;

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.password-form {
  margin-top: 24px;

  .el-form-item {
    margin-bottom: 26px;
  }

  .el-form-item:last-child {
    margin-bottom: 0;
  }

  .el-form-item__label {
    height: 20px;
    padding: 0 !important;
    margin-bottom: 4px;
    color: var(--app-text-color);
    font-size: var(--app-font-size);
    font-weight: 400;
    line-height: 20px;
  }

  .el-form-item__label::before {
    margin-right: 4px !important;
  }

  .el-form-item__content {
    line-height: normal;
  }

  .el-input__wrapper {
    min-height: 34px;
    padding: 0 12px;
    background: var(--app-surface-color);
    border-radius: 2px;
    box-shadow: 0 0 0 1px var(--app-border-color) inset;
  }

  .el-input__wrapper:hover {
    box-shadow: 0 0 0 1px var(--el-border-color-hover) inset;
  }

  .el-input__wrapper.is-focus {
    box-shadow: 0 0 0 1px var(--password-brand-color) inset;
  }

  .el-form-item.is-error .el-input__wrapper {
    box-shadow: 0 0 0 1px var(--password-brand-color) inset;
  }

  .el-form-item__error {
    top: calc(100% + 2px);
    padding-top: 0;
    font-size: 12px;
    line-height: 18px;
  }
}

.password-form__label {
  display: inline-flex;
  align-items: center;

  i {
    margin-right: 4px;
    color: var(--el-color-danger);
    font-style: normal;
  }
}

.password-dialog__footer {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  .el-button {
    min-width: 110px;
    height: 40px;
    margin: 0;
    padding: 0 18px;
    font-weight: 500;
    border-radius: 4px;
  }

  .el-button--primary {
    color: #fff;
    background: var(--password-brand-color);
    border-color: var(--password-brand-color);

    &:hover,
    &:focus-visible {
      color: #fff;
      background: #ff974a;
      border-color: #ff974a;
    }

    &:active {
      color: #fff;
      background: #e36f19;
      border-color: #e36f19;
    }

    &.is-disabled,
    &.is-disabled:hover,
    &.is-disabled:focus {
      color: #fff;
      background: #ffc47b;
      border-color: #ffc47b;
    }
  }
}
</style>
